/* eslint-disable @typescript-eslint/no-explicit-any */

import { GridColDef } from '@mui/x-data-grid-premium';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PageNameType =
 | 'results'
 | 'customers'
 | 'vehicles'
 | 'drivers'
 | 'products'
 | 'destPoint';

export type ClientConfigurationItem = {
 Id: string;
 Name: string;
 Parameter: string;
 Type: string;
 Value: string;
};

// COLUMNS, FILTERS

export type FilterFieldsType = {
 fields: {
  [key: string]:
   | undefined
   | null
   | string
   | number
   | Array<{ [key: string]: string | number }>;
 };
 operators: { [key: string]: string | null };
};

export type ColPresetType = {
 name: string;
 columns: GridColDef[];
 filters?: FilterFieldsType;
 isPro?: boolean;
 limit?: {
  [key: string]: Array<{
   limit: string;
   value: string;
   color: string;
  }>;
 };
};

// export type ColPresetsType = {
//  name: string;
//  data: ColPresetType[];
// };

export type FilterPresetType = {
 name?: string;
 data: FilterFieldsType;
};

// FIELDS

export type FieldPresetType = {
 [key: string]: {
  sidebar: string[];
  edit: string[];
 };
};

type ClientConfigurationState = {
 clientConfiguration: ClientConfigurationItem | null;
 clientConfigurations: ClientConfigurationItem[] | null;
 createdConfiguration: ClientConfigurationItem | null;
 loading: { [key: string]: boolean | null };
 success: { [key: string]: boolean | null };
 rowCount: string;
 //
 presetsColumns: { [key: string]: ColPresetType[] } | null;
 currentColumnsPreset: { [key: string]: ColPresetType | null } | null;
 tempColumnsPreset: { [key: string]: ColPresetType | null } | null;
 fieldPresets: FieldPresetType | null;
};

// enum record

const initialState: ClientConfigurationState = {
 clientConfiguration: null,
 clientConfigurations: null,
 createdConfiguration: null,
 rowCount: '0',
 loading: {},
 success: {},
 presetsColumns: null,
 currentColumnsPreset: null,
 tempColumnsPreset: null,
 fieldPresets: null,
};

const clientConfigurationSlice = createSlice({
 name: 'clientConfiguration',
 initialState,
 reducers: {
  // state

  changeCurrentPreset(
   state,
   action: PayloadAction<{
    page: PageNameType;
    type: 'columns' | 'fields';
    preset: ColPresetType;
    temp: boolean;
   }>,
  ) {
   const { page, type, preset, temp } = action.payload;

   // refactor (remove) type, because we now have only one (filters moved inside columns preset)

   if (type === 'columns') {
    return {
     ...state,
     ...(temp
      ? {
         tempColumnsPreset: {
          ...state.tempColumnsPreset,
          [page]: preset,
         },
        }
      : {
         currentColumnsPreset: {
          ...state.currentColumnsPreset,
          [page]: preset,
         },
        }),
    };
   }

   return state;
  },

  getCurrentPresetStart(
   state,
   action: PayloadAction<{ table: string; type: 'columns' | 'fields' }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, getCurrentPreset: true },
   };
  },

  getCurrentPresetSuccess(state, action: PayloadAction<any>) {
   const o = {
    ...state,
    ...(action.payload.currentColumnsPreset
     ? {
        currentColumnsPreset: {
         ...state.currentColumnsPreset,
         [action.payload.page]: action.payload.currentColumnsPreset,
        },
       }
     : {}),
    // currentPreset: action.payload.preset,
    success: { ...state.success, getCurrentPreset: true },
    loading: { ...state.loading, getCurrentPreset: null },
   };

   return o;
  },

  getCurrentPresetFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getCurrentPreset: null },
    loading: { ...state.loading, getCurrentPreset: null },
   };
  },

  // single

  getClientConfigurationStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, getClientConfiguration: true },
   };
  },

  getClientConfigurationSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    clientConfiguration: action.payload.data,
    rowCount: action.payload.rowCount,
    success: { ...state.success, getClientConfiguration: true },
    loading: { ...state.loading, getClientConfiguration: null },
   };
  },

  getClientConfigurationFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getClientConfiguration: null },
    loading: { ...state.loading, getClientConfiguration: null },
   };
  },

  getClientConfigurationFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getClientConfiguration: null },
    loading: { ...state.loading, getClientConfiguration: null },
   };
  },

  // multiple

  getClientConfigurationsStart(
   state,
   action: PayloadAction<{ cached?: boolean }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, getClientConfigurations: true },
   };
  },

  getClientConfigurationsSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    clientConfigurations: action.payload.data,
    presetsColumns: action.payload.presetsColumns,
    fieldPresets: action.payload.fieldPresets,
    rowCount: action.payload.rowCount,
    success: { ...state.success, getClientConfigurations: true },
    loading: { ...state.loading, getClientConfigurations: null },
   };
  },

  getClientConfigurationsFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getClientConfigurations: false },
    loading: { ...state.loading, getClientConfigurations: null },
   };
  },

  getClientConfigurationsFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getClientConfigurations: null },
    loading: { ...state.loading, getClientConfigurations: null },
   };
  },

  // create

  createClientConfigurationStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, createClientConfiguration: true },
   };
  },

  createClientConfigurationSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    createdConfiguration: action.payload.data,
    rowCount: action.payload.rowCount,
    success: { ...state.success, createClientConfiguration: true },
    loading: { ...state.loading, createClientConfiguration: null },
   };
  },

  createClientConfigurationFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, createClientConfiguration: false },
    loading: { ...state.loading, createClientConfiguration: null },
   };
  },

  createClientConfigurationFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, createClientConfiguration: null },
    loading: { ...state.loading, createClientConfiguration: null },
   };
  },

  // update

  updateClientConfigurationStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, updateClientConfiguration: true },
   };
  },

  updateClientConfigurationSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    updatedConfiguration: action.payload.data,
    rowCount: action.payload.rowCount,
    success: { ...state.success, updateClientConfiguration: true },
    loading: { ...state.loading, updateClientConfiguration: null },
   };
  },

  updateClientConfigurationFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, updateClientConfiguration: false },
    loading: { ...state.loading, updateClientConfiguration: null },
   };
  },

  updateClientConfigurationFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, updateClientConfiguration: null },
    loading: { ...state.loading, updateClientConfiguration: null },
   };
  },

  // delete

  deleteClientConfigurationStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, deleteClientConfiguration: true },
   };
  },

  deleteClientConfigurationSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    deletedConfiguration: action.payload.data,
    rowCount: action.payload.rowCount,
    success: { ...state.success, deleteClientConfiguration: true },
    loading: { ...state.loading, deleteClientConfiguration: null },
   };
  },

  deleteClientConfigurationFailure(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, deleteClientConfiguration: false },
    loading: { ...state.loading, deleteClientConfiguration: null },
   };
  },

  deleteClientConfigurationFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, deleteClientConfiguration: null },
    loading: { ...state.loading, deleteClientConfiguration: null },
   };
  },
 },
});

export const {
 changeCurrentPreset,
 getCurrentPresetStart,
 getCurrentPresetSuccess,
 getCurrentPresetFailure,
 //
 getClientConfigurationStart,
 getClientConfigurationSuccess,
 getClientConfigurationFailure,
 getClientConfigurationFinish,
 getClientConfigurationsStart,
 getClientConfigurationsSuccess,
 getClientConfigurationsFailure,
 getClientConfigurationsFinish,
 createClientConfigurationStart,
 createClientConfigurationSuccess,
 createClientConfigurationFailure,
 createClientConfigurationFinish,
 updateClientConfigurationStart,
 updateClientConfigurationSuccess,
 updateClientConfigurationFailure,
 updateClientConfigurationFinish,
 deleteClientConfigurationStart,
 deleteClientConfigurationSuccess,
 deleteClientConfigurationFailure,
 deleteClientConfigurationFinish,
} = clientConfigurationSlice.actions;

export default clientConfigurationSlice.reducer;
